/**
 * useChat.js
 *
 * React hook for chat messaging on a specific channel.
 * Uses the shared Socket.io connection from ChatContext.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useChatContext } from '../contexts/ChatContext';
import * as chatApi from '../api/chat';

export default function useChat(channelId) {
  const { user } = useAuthStore();
  const { getSocket, setActiveChannelId, refreshUnread, setChannelUnreadCounts, setGlobalUnreadCount } = useChatContext();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef(null);
  const lastTypingEmit = useRef(0);
  const typingClearTimers = useRef({});

  // Join / leave channel room
  useEffect(() => {
    if (!channelId) return;
    const socket = getSocket();
    if (!socket?.connected) return;

    socket.emit('chat:join', { channelId });
    setActiveChannelId(channelId);

    // Mark as read when opening
    chatApi.markAsRead(channelId).then(() => {
      setChannelUnreadCounts(prev => {
        const updated = { ...prev, [channelId]: 0 };
        const total = Object.values(updated).reduce((s, c) => s + c, 0);
        setGlobalUnreadCount(total);
        return updated;
      });
    }).catch(() => {});

    return () => {
      socket.emit('chat:leave', { channelId });
      setActiveChannelId(null);
    };
  }, [channelId, getSocket]);

  // Listen for channel-specific events
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !channelId) return;

    const onNewMessage = ({ message }) => {
      if (message.channel_id !== channelId) return;
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === message.id)) return prev;
        return [...prev, message];
      });
      // Auto mark as read since we're viewing this channel
      chatApi.markAsRead(channelId).catch(() => {});
      socket.emit('chat:markRead', { channelId });
    };

    const onMessageEdited = ({ messageId, content, editedAt }) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content, is_edited: true, edited_at: editedAt } : m
      ));
    };

    const onMessageDeleted = ({ messageId }) => {
      setMessages(prev => prev.filter(m => m.id !== messageId));
    };

    const onTyping = ({ userId, userName, channelId: typCh }) => {
      if (typCh !== channelId || userId === user?.id) return;
      setTypingUsers(prev => ({ ...prev, [userId]: userName }));
      // Auto-clear after 3 seconds
      if (typingClearTimers.current[userId]) clearTimeout(typingClearTimers.current[userId]);
      typingClearTimers.current[userId] = setTimeout(() => {
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
      }, 3000);
    };

    const onStopTyping = ({ userId, channelId: typCh }) => {
      if (typCh !== channelId) return;
      setTypingUsers(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      if (typingClearTimers.current[userId]) {
        clearTimeout(typingClearTimers.current[userId]);
        delete typingClearTimers.current[userId];
      }
    };

    socket.on('chat:newMessage', onNewMessage);
    socket.on('chat:messageEdited', onMessageEdited);
    socket.on('chat:messageDeleted', onMessageDeleted);
    socket.on('chat:typing', onTyping);
    socket.on('chat:stopTyping', onStopTyping);

    return () => {
      socket.off('chat:newMessage', onNewMessage);
      socket.off('chat:messageEdited', onMessageEdited);
      socket.off('chat:messageDeleted', onMessageDeleted);
      socket.off('chat:typing', onTyping);
      socket.off('chat:stopTyping', onStopTyping);
      // Clear typing timers
      Object.values(typingClearTimers.current).forEach(clearTimeout);
      typingClearTimers.current = {};
    };
  }, [channelId, getSocket, user?.id]);

  // Load initial messages
  useEffect(() => {
    if (!channelId) { setMessages([]); return; }
    setLoading(true);
    setMessages([]);
    setHasMore(true);
    chatApi.getMessages(channelId, { limit: 50 })
      .then(data => {
        setMessages(data.messages || []);
        setHasMore((data.messages || []).length >= 50);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [channelId]);

  // Load older messages
  const loadMore = useCallback(async () => {
    if (!channelId || !hasMore || loading) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoading(true);
    try {
      const data = await chatApi.getMessages(channelId, { limit: 50, before: oldest.created_at });
      const older = data.messages || [];
      if (older.length < 50) setHasMore(false);
      setMessages(prev => [...older, ...prev]);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [channelId, hasMore, loading, messages]);

  // Send message
  const sendMsg = useCallback(async (data) => {
    if (!channelId) return;
    const result = await chatApi.sendMessage(channelId, data);
    // The socket event will add the message to state
    return result.message;
  }, [channelId]);

  // Edit message
  const editMsg = useCallback(async (messageId, content) => {
    return chatApi.editMessage(messageId, content);
  }, []);

  // Delete message
  const deleteMsg = useCallback(async (messageId) => {
    return chatApi.deleteMessage(messageId);
  }, []);

  // Typing indicator (debounced: emit max once every 2s)
  const emitTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket?.connected || !channelId) return;
    const now = Date.now();
    if (now - lastTypingEmit.current > 2000) {
      socket.emit('chat:typing', { channelId });
      lastTypingEmit.current = now;
    }
    // Schedule stop typing
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:stopTyping', { channelId });
    }, 3000);
  }, [channelId, getSocket]);

  // Upload file
  const uploadAttachment = useCallback(async (file) => {
    if (!channelId) return;
    const formData = new FormData();
    formData.append('file', file);
    return chatApi.uploadFile(channelId, formData);
  }, [channelId]);

  return {
    messages,
    loading,
    hasMore,
    loadMore,
    sendMessage: sendMsg,
    editMessage: editMsg,
    deleteMessage: deleteMsg,
    emitTyping,
    typingUsers,
    uploadAttachment,
  };
}
